import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpenText, ArrowSquareOut, ArrowRight } from "@phosphor-icons/react";
import api from "@/api";

/**
 * Aggregator that lists every paper released across all sessions in this
 * session's block. Only rendered when:
 *   - the block has more than one session, AND
 *   - at least one other session in the block has released papers.
 *
 * Delegates only ever see released items (server enforces). Admins see every
 * item (released + hidden) so they can preview the full block reading list.
 */
export function BlockPapersBlock({ sessionId, isAdmin }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/sessions/${sessionId}/block-resources`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ entries: [] }); });
    return () => { alive = false; };
  }, [sessionId]);

  if (!data) return null;
  const others = (data.entries || []).filter((e) => !e.is_current && e.items.length > 0);
  // Single-session block, or no released papers anywhere else - render nothing.
  if (others.length === 0 || data.session_count <= 1) return null;

  const totalPapers = others.reduce((sum, e) => sum + e.items.length, 0);

  return (
    <details
      className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm group"
      data-testid="block-papers-aggregator"
    >
      <summary className="cursor-pointer flex items-center justify-between gap-2 list-none">
        <div className="flex items-center gap-2 min-w-0">
          <BookOpenText size={18} weight="duotone" className="text-[#000000] flex-shrink-0" />
          <div className="min-w-0">
            <h2 className="font-display text-base sm:text-lg leading-tight">
              All papers from this block
            </h2>
            {data.block_track && (
              <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C] mt-0.5">
                {data.block_track}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">
            {others.length} talk{others.length === 1 ? "" : "s"} · {totalPapers} paper{totalPapers === 1 ? "" : "s"}
          </span>
          <ArrowRight
            size={14}
            className="text-[#7A828C] transition-transform group-open:rotate-90"
          />
        </div>
      </summary>

      <div className="mt-4 space-y-5">
        {others.map((entry) => (
          <div
            key={entry.session_id}
            className="border-t border-[#E5E9F0] pt-4 first:border-t-0 first:pt-0"
            data-testid={`block-papers-entry-${entry.session_id}`}
          >
            <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
              <Link
                to={`/program/${entry.session_id}`}
                className="font-display text-sm text-[#1c1c24] hover:text-[#000000] hover:underline"
              >
                {entry.session_title}
              </Link>
              {entry.speakers.length > 0 && (
                <span className="text-xs text-[#7A828C] truncate">
                  {entry.speakers.map((sp) => sp.name).join(", ")}
                </span>
              )}
            </div>
            {isAdmin && entry.released_count < entry.total_count && (
              <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C] mb-2">
                {entry.released_count}/{entry.total_count} released
              </div>
            )}
            <ol className="space-y-2">
              {entry.items.map((it, idx) => (
                <li
                  key={it.id}
                  className={`text-sm leading-relaxed flex gap-2 ${
                    isAdmin && !it.released ? "opacity-60" : ""
                  }`}
                  data-testid={`block-papers-item-${it.id}`}
                >
                  <span className="font-mono-time text-xs text-[#7A828C] flex-shrink-0 mt-0.5 w-6">
                    {idx + 1}.
                  </span>
                  <div className="min-w-0 flex-1 whitespace-pre-line break-words text-[#1c1c24]">
                    {renderLinks(it.text)}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </details>
  );
}

const URL_RX = /(https?:\/\/[^\s<>"']+)/gi;
const DOI_RX = /(?:doi:\s*)(10\.\d{4,9}\/[-._;()/:A-Z0-9]+)/gi;
const PMID_RX = /\bPMID:\s*(\d{4,})/gi;
const PMCID_RX = /\bPMCID:\s*(PMC\d+)/gi;

function renderLinks(text) {
  const matches = [];
  const collect = (rx, makeUrl, label) => {
    let m;
    rx.lastIndex = 0;
    while ((m = rx.exec(text)) !== null) {
      matches.push({ start: m.index, end: m.index + m[0].length, url: makeUrl(m), label: label(m) });
    }
  };
  collect(URL_RX, (m) => m[0], (m) => m[0]);
  collect(DOI_RX, (m) => `https://doi.org/${m[1]}`, (m) => m[0]);
  collect(PMID_RX, (m) => `https://pubmed.ncbi.nlm.nih.gov/${m[1]}/`, (m) => m[0]);
  collect(PMCID_RX, (m) => `https://www.ncbi.nlm.nih.gov/pmc/articles/${m[1]}/`, (m) => m[0]);
  matches.sort((a, b) => a.start - b.start);

  const dedup = [];
  let lastEnd = -1;
  for (const m of matches) {
    if (m.start >= lastEnd) {
      dedup.push(m);
      lastEnd = m.end;
    }
  }
  if (dedup.length === 0) return text;

  const out = [];
  let cursor = 0;
  dedup.forEach((m, i) => {
    if (m.start > cursor) out.push(text.slice(cursor, m.start));
    out.push(
      <a
        key={`l${i}`}
        href={m.url}
        target="_blank"
        rel="noreferrer noopener"
        className="text-[#000000] hover:underline inline-flex items-center gap-0.5"
      >
        {m.label}
        <ArrowSquareOut size={11} className="inline" />
      </a>
    );
    cursor = m.end;
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}
